import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Box
} from '@mui/material'
import { Typography, Input, Button, IconButton } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import Close24 from '@evocolabs/icons/lib/Close24'
import { useSchedule } from '@/components/Schedule/ScheduleRoot'
import validateEmail from '@/src/utils/validateEmail'
import { useCalendar } from '@/components/Schedule/Calendar/CalendarProvider'
import { useBackend } from '@/src/providers/BackendProvider'

const Wrapper = styled(Dialog)`
  & .MuiDialog-paper {
    width: 600px;
    padding: 40px 56px 32px;
    border-radius: 12px;
    ${(props) => props.theme.breakpoints.down('md')} {
      padding: 32px 0;
      margin: 32px 0;
    }
  }
  & .MuiDialog-paperScrollBody {
    max-width: calc(100% - 32px);
  }
`
const Title = styled(DialogTitle)`
  padding: 0 24px;
`
const Content = styled(DialogContent)`
  padding: 0 24px 32px;
  margin-top: 24px;
`
const Actions = styled(DialogActions)`
  padding: 0 24px;
  justify-content: center;
`
const IconWrapper = styled(Box)`
  position: absolute;
  top: 8px;
  right: 8px;
`

const InfoDialog = ({ open, setOpen }) => {
  const {
    viewId,
    setViewId,
    history,
    info,
    setInfo,
    params,
    schedule,
    reschedule,
    setErrorOpen
  } = useSchedule()
  const { userDetails, signedin, refreshAppointment } = useBackend()
  const [error, setError] = useState({
    email: '',
    phone: ''
  })

  const validatePhone = (phone) => {
    const re = /\d{3}-\d{3}-\d{4}/g
    return re.test(String(phone).toLowerCase())
  }

  // handle auto-complete phone input
  const phoneFormatting = (ele, restore) => {
    let newNumber
    const number = ele.value.replace(/\D/g, '')
    // automatically add dashes
    if (restore !== true) {
      if (number.length > 2) {
        // matches: 123 || 123-4 || 123-45
        newNumber = number.substring(0, 3) + '-'
        if (number.length === 4 || number.length === 5) {
          // matches: 123-4 || 123-45
          newNumber += number.substring(3)
        } else if (number.length > 5) {
          // matches: 123-456 || 123-456-7 || 123-456-789
          newNumber += number.substring(3, 6) + '-'
        }
        if (number.length > 6) {
          // matches: 123-456-7 || 123-456-789 || 123-456-7890
          newNumber += number.substring(6)
        }
      } else {
        newNumber = number
      }
    } else if (restore === true) {
      // delete - not join with -
      if (number.length > 3) {
        // matches: 123-4 || 123-45
        newNumber = number.substring(0, 3) + '-'
        if (number.length === 4 || number.length === 5 || number.length === 6) {
          // matches: 123-4 || 123-45 || 123-456
          newNumber += number.substring(3)
        } else if (number.length > 6) {
          // matches:  123-456-7 || 123-456-789
          newNumber += number.substring(3, 6) + '-'
        }
        if (number.length > 6) {
          // matches: 123-456-7 || 123-456-789 || 123-456-7890
          newNumber += number.substring(6)
        }
      } else {
        newNumber = number
      }
    }
    ele.value = newNumber.length > 12 ? newNumber.substring(0, 12) : newNumber
    setInfo((lastestProps) => {
      return { ...lastestProps, phone: ele.value }
    })
  }

  const phoneNumberCheck = (field, e) => {
    const keyCode = e.keyCode
    const keyString = String.fromCharCode(keyCode)
    let pressDelete = false
    const dashKey = 189
    const deleteKey = [8, 46]
    const directionKey = [33, 34, 35, 36, 37, 38, 39, 40]
    const selectionEnd = field.selectionEnd

    // delete key was pressed
    if (deleteKey.indexOf(keyCode) > -1) {
      pressDelete = true
    }

    // only force formatting is a number or delete key was pressed
    if (keyString.match(/^\d+$/) || pressDelete) {
      phoneFormatting(field, pressDelete)
    } else if (directionKey.indexOf(keyCode) > -1) {
      // do nothing for direction keys, keep their default actions
      // do nothing
    } else if (dashKey === keyCode) {
      if (selectionEnd === field.value.length) {
        field.value = field.value.slice(0, -1)
      } else {
        field.value =
          field.value.substring(0, selectionEnd - 1) +
          field.value.substr(selectionEnd)
        field.selectionEnd = selectionEnd - 1
      }
    } else {
      // all other non numerical key presses, remove their value
      e.preventDefault()
      phoneFormatting(field, 'revert')
    }
  }

  const handleOnKeyUp = (e) => {
    const field = document.getElementsByName('schedule-phone')[0]
    phoneNumberCheck(field, e)
  }

  const handleChange = (prop) => (event) => {
    setInfo({
      ...info,
      [prop]: event.target.value
    })
    setError({
      ...error,
      [prop]: ''
    })
  }

  async function handleSubmit() {
    if (signedin) {
      setInfo({
        ...info,
        email: userDetails.user_name
      })
    }
    if (!signedin && !validateEmail(info.email)) {
      setError({
        ...error,
        email: 'Invalid email address'
      })
      return
    }
    if (!validatePhone(info.phone)) {
      setError({
        ...error,
        phone: 'Invalid phone number'
      })
      return
    }
    const token = params.token
      ? params.token
      : JSON.parse(window.sessionStorage.getItem('schedule-token'))
    const id = params.id
      ? params.id
      : JSON.parse(window.sessionStorage.getItem('appointment-id'))
    let res
    if (token) {
      res = await reschedule(token, id)
    } else {
      res = await schedule()
    }
    await refreshAppointment()
    if (res.status < 300) {
      window.sessionStorage.setItem(
        'schedule-token',
        JSON.stringify(res.data.token)
      )
      window.sessionStorage.setItem(
        'appointment-id',
        JSON.stringify(res.data.result.id)
      )
      history.push(viewId)
      setViewId(viewId + 1)
      return res
    } else {
      setOpen(false)
      setErrorOpen(true)
      return res
    }
  }

  return (
    <Wrapper open={open} scroll="body">
      <IconWrapper>
        <IconButton onClick={() => setOpen(false)}>
          <Close24 />
        </IconButton>
      </IconWrapper>
      <Title>
        <Typography variant="h3" sx={{ fontSize: '22px' }}>
          Provide your contact information
        </Typography>
      </Title>
      <Content>
        <Input
          value={info.name}
          placeholder="Name"
          onChange={handleChange('name')}
          fullWidth
          controlSx={{ mt: '16px' }}
        />
        {!signedin && (
          <Input
            value={info.email}
            placeholder="Email"
            onChange={handleChange('email')}
            fullWidth
            controlSx={{ mt: '16px' }}
            errormsg={error.email}
          />
        )}
        <Input
          name="schedule-phone"
          value={info.phone}
          placeholder="Phone number"
          onChange={handleChange('phone')}
          onKeyUp={handleOnKeyUp}
          fullWidth
          controlSx={{ mt: '16px' }}
          errormsg={error.phone}
        />
      </Content>
      <Actions>
        <Button
          size="large"
          style={{ width: '184px' }}
          handleClick={handleSubmit}
          loadable
        >
          submit
        </Button>
      </Actions>
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.grey[600],
          mt: '8px',
          fontSize: '12px',
          padding: '0 24px'
        }}
      >
        Before continuing, please ensure you have read and accepted Orka's{' '}
        <a style={{ fontWeight: 600 }} href="/legal/TermsOfService">
          TERMS OF USE
        </a>{' '}
        and{' '}
        <a style={{ fontWeight: 600 }} href="/legal/PrivacyPolicy">
          PRIVACY POLICY
        </a>
        . Orka respects your privacy, and will keep your data safe and secure.
      </Typography>
    </Wrapper>
  )
}

export default InfoDialog
